// "When will you be there?" — and the four honest answers to it. Lifted from
// the Andon app's `EtaSheet`, narrowed to what the frame draws.
//
// THE FOUR ANSWERS. Two, five and fifteen minutes, and "after my shift". The
// three chips are the common case and two taps is worth a lot on a stopped
// line. The fourth is the one answer this client refuses to invent a number
// for: the shift calendar is in Odoo, CNC knocks off at a different hour from
// GRC Pouring, and a figure made up here would be indistinguishable on the
// record from one a responder chose. It sends no minutes and a basis instead;
// what comes back on the row is whatever Odoo could resolve, and the record
// says which.
//
// WHAT THE LIFT DROPPED. The Andon app's "Longer" stepper — any minute count,
// five at a time — did not come across; the frame fixes the set at the three
// chips plus end of shift, and a stepper beside them was the one control on
// the sheet that needed explaining.
//
// AND HANDING IT ON GOES THROUGH HERE, ALWAYS.
//
// Handing over used to be reachable straight off the unowned face, with no
// time given and no clock stopped: the owner changed, the card stayed in New
// still climbing the ladder, and whoever it landed on inherited a commitment
// nobody had asked them about. A handover passes on a response time, so there
// has to be one to pass on — which makes this sheet the door to it. Both
// routes end in the same pair of acts in the same order: take it, which stops
// the operator's clock on a number somebody chose, then choose who it goes to.

import { useEffect, useState } from 'react';
import type { AndonAlertVM } from '@/api/andon/types';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { ackLabel } from '@/lib/actions';

/** The fast path. Mirrors `ETA_CHIPS` in the addon, which is advisory there. */
const CHIPS = [2, 5, 15] as const;

/**
 * Which of the two acts the person came here for.
 *
 * `take` is the ordinary one and `handover` is somebody who has already decided
 * they are the wrong person. The sheet is the same sheet — the ETA question is
 * asked identically, because it is the same promise either way — and only the
 * wording and where the tap lands afterwards differ.
 */
export type EtaIntent = 'take' | 'handover';

export function EtaSheet({
  alert,
  open,
  onClose,
  onSubmit,
  onHandover,
  busy = false,
  intent = 'take',
}: {
  /** Who raised it and who holds it — all the sheet reads. See `TakeSheet`. */
  alert: Pick<AndonAlertVM, 'raisedBy' | 'owner'>;
  open: boolean;
  onClose: () => void;
  /** `minutes` is null exactly when `basis` is `end_of_shift`. */
  onSubmit: (minutes: number | null, basis: 'chosen' | 'end_of_shift') => void;
  /**
   * TAKE IT ON THIS TIME, THEN OPEN THE HANDOVER. One gesture, two acts, in
   * the order that leaves nothing running: the caller takes the andon with
   * exactly these numbers and opens the responder list on the way back.
   */
  onHandover: (minutes: number | null, basis: 'chosen' | 'end_of_shift') => void;
  busy?: boolean;
  intent?: EtaIntent;
}) {
  const t = useT();
  /** null = nothing picked yet; 'shift' = after my shift. */
  const [pick, setPick] = useState<number | 'shift' | null>(null);

  // Reopening starts clean. A sheet that remembers the last answer is a sheet
  // that sends it by accident on the next andon.
  useEffect(() => {
    if (open) setPick(null);
  }, [open]);

  const handing = intent === 'handover';
  const takeLabel = ackLabel(alert, t);
  const shift = pick === 'shift';
  const chosen = typeof pick === 'number' ? pick : null;
  const ready = chosen != null || shift;
  const first = alert.raisedBy.split(' ')[0];

  const submit = () => (handing ? onHandover : onSubmit)(shift ? null : chosen, shift ? 'end_of_shift' : 'chosen');

  return (
    <Sheet open={open} onClose={onClose} title={handing ? t.etaHandItOnTitleT : `${takeLabel} — ${t.etaWhenThereT}`}>
      {/* IT SAYS WHO READS IT. A person is standing at a stopped machine
          reading this number; nothing escalates on a missed ETA any more,
          and the sheet does not claim otherwise. */}
      <p style={{ margin: '0 0 14px', font: '500 12px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
        {handing ? `${first} ${t.etaHandingLeadT}` : `${first} ${t.etaWaitingLeadT}`}
      </p>

      <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
        {CHIPS.map((eta) => {
          const on = pick === eta;
          return (
            <button
              key={eta}
              type="button"
              className="press-soft"
              aria-pressed={on}
              disabled={busy}
              onClick={() => setPick(eta)}
              style={{
                flex: 1,
                height: 56,
                borderRadius: 14,
                background: on ? 'var(--soft)' : 'var(--surface)',
                border: `${on ? 1.5 : 1}px solid ${on ? 'var(--pos)' : 'var(--line)'}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <span className="numeric" style={{ font: "700 17px 'Space Grotesk',sans-serif", color: on ? 'var(--pos)' : 'var(--ink)' }}>
                {eta}
              </span>
              <span style={{ font: '600 9.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.minT}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="press-soft"
          aria-pressed={shift}
          disabled={busy}
          onClick={() => setPick('shift')}
          style={{
            flex: 1.5,
            height: 56,
            borderRadius: 14,
            background: shift ? 'var(--soft)' : 'var(--surface)',
            border: `${shift ? 1.5 : 1}px solid ${shift ? 'var(--pos)' : 'var(--line)'}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            padding: '0 6px',
          }}
        >
          <span style={{ font: "700 13px 'Space Grotesk',sans-serif", color: shift ? 'var(--pos)' : 'var(--ink)', textAlign: 'center' }}>{t.etaAfterShiftT}</span>
          {/* No arrival time on this one, because the app does not know it.
              Printing a guess here is exactly the fabrication the basis
              column exists to keep off the record. */}
          <span style={{ font: '600 9.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.etaOdooWorksOutT}</span>
        </button>
      </div>

      <button
        type="button"
        className="press-soft"
        disabled={!ready || busy}
        onClick={submit}
        style={{
          width: '100%',
          height: 52,
          borderRadius: 15,
          background: ready ? 'var(--pos)' : 'var(--surface2)',
          color: ready ? '#fff' : 'var(--sub)',
          border: 0,
          font: "700 14px 'Space Grotesk',sans-serif",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy
          ? t.etaTakingT
          : handing
            ? ready
              ? `${takeLabel} — ${t.etaThenChooseT}`
              : `${takeLabel} — ${t.etaWhenThereT}`
            : shift
              ? `${takeLabel} — ${t.etaAfterShiftLowerT}`
              : chosen == null
                ? `${takeLabel} — ${t.etaWhenThereT}`
                : `${takeLabel} — ${t.etaThereInT} ${chosen} ${t.minT}`}
      </button>

      <p style={{ margin: '10px 2px 0', textAlign: 'center', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>
        {handing ? t.etaHandingFootT : t.etaTakingFootT}
      </p>
    </Sheet>
  );
}
